import React, { useState } from 'react';
import PageHeading from '../../components/PageHeading';
import FeatureIntro from '../../components/FeatureIntro';
import Loading from '../../components/Loading';
import { useInsightsQuery, setInsightsQuery } from '../../lib/Queries';
import useBusinessPostApi from '../../lib/useBusinessPostApi';
import {
    XMarkIcon,
    LightBulbIcon,
    CheckCircleIcon,
    NoSymbolIcon,
} from '@heroicons/react/24/outline';
import DropdownButton from '../../components/DropdownButton';
import SectionPicker from '../../components/SectionPicker';
import MagicWandIcon from '~/components/icons/MagicWandIcon.jsx';
import { BoltIcon } from '@heroicons/react/24/outline/index.js';
import emitter, { EmitterEventTypes } from '../../lib/emitter';
import * as Toast from '../../lib/Toast.js';

const InsightCard = ({ insight, onDismiss }) => {
    return (
        <div className="bg-white rounded-xl p-6 flex flex-col gap-3">
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1">
                    <div className="flex-1">
                        <div className="mb-4 flex gap-2">
                            {insight.confidence >= 8 ? (
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-medium" style={{ backgroundColor: '#F2F7F3', borderColor: '#BBD7C2', color: '#549A68' }}>
                                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: '#549A68' }}></span>
                                    High Confidence
                                </span>
                            ) : insight.confidence >= 7 ? (
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-medium" style={{ backgroundColor: '#FEF7F1', borderColor: '#F7D6B5', color: '#E89746' }}>
                                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: '#E89746' }}></span>
                                    Medium Confidence
                                </span>
                            ) : null}
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium" style={{ backgroundColor: '#F9F8F6', borderColor: '#E3E1DB', color: '#5F636E' }}>
                                {insight.impact >= 7 ? <><BoltIcon className="w-3 h-3 mr-1" /> Quick Win</> : insight.impact >= 6 ? 'Moderate Effort' : 'Low Effort'}
                            </span>
                        </div>
                        <p className="font-bold">
                            {insight.title}
                        </p>
                        <p className="">
                            {insight.idea}
                        </p>
                    </div>
                </div>
                {onDismiss && (
                    <DropdownButton
                        btnClassName="text-gray-500 hover:text-gray-600 p-2 rounded-full bg-gray-100"
                        btnContent={
                            <XMarkIcon className="w-4 h-4" />
                        }
                        options={[
                            {
                                name: 'Mark as completed',
                                icon: null,
                                onClick: () => {
                                    onDismiss(insight.id, 'completed');
                                    return true;
                                },
                            },
                            {
                                name: 'Not applicable',
                                icon: null,
                                onClick: () => {
                                    onDismiss(insight.id, 'dismissed');
                                    return true;
                                },
                            },
                        ]}
                    />
                )}
            </div>
            {insight.prompt && insight.cta && (
                <div className="">
                    <button
                        type="button"
                        className="btn-pill flex items-center gap-x-2"
                        onClick={() => emitter.emit(EmitterEventTypes.OPEN_ASSISTANT_WITH_PROMPT, { prompt: insight.prompt })}
                    >
                        <MagicWandIcon />
                        {insight.cta}
                    </button>
                </div>
            )}
        </div>
    );
};

const InsightsIndexPage = () => {
    const { data, isLoading } = useInsightsQuery();
    const insights = data?.insights || [];
    const dismissedInsights = data?.dismissedInsights || [];
    const [section, setSection] = useState('active');
    const { save } = useBusinessPostApi('/insights/{id}/status', { noToast: true });

    function handleDismiss(insightId, status) {
        const route = `/insights/${insightId}/status`;
        const { save: saveDismiss } = { save: (d) => {
            return import('../../lib/Api').then(({ post, businessApiRoute }) => {
                return post(businessApiRoute(route), d);
            });
        }};

        saveDismiss({ status }).then(result => {
            setInsightsQuery(result);
            Toast.success('Insight dismissed');
        });
    }

    if (isLoading) {
        return <Loading />;
    }

    return (
        <div>
            <PageHeading heading="Insights" />
            <FeatureIntro>
                Personalized recommendations to help grow your business, based on your setup, metrics, and website.
            </FeatureIntro>

            <div className="flex mb-6">
                <SectionPicker
                    value={section}
                    onChange={setSection}
                    options={[
                        { value: 'active', label: 'Active' },
                        { value: 'dismissed', label: 'Dismissed' },
                    ]}
                />
            </div>

            {section === 'active' && (
                <div className="max-w-2xl flex flex-col gap-5">
                    {insights.length === 0 && (
                        <div className="panel py-4 px-4 text-gray-500">
                            No new insights right now. Check back again tomorrow!<br /><br />
                            Every day we'll analyze your business's setup, usage metrics, website, and upcoming holidays to generate growth recommendations.
                        </div>
                    )}
                    {insights.map(insight => (
                        <InsightCard
                            key={insight.id}
                            insight={insight}
                            onDismiss={handleDismiss}
                        />
                    ))}
                </div>
            )}

            {section === 'dismissed' && (
                <div className="max-w-2xl flex flex-col gap-5">
                    {dismissedInsights.length === 0 && (
                        <div className="panel py-4 px-4 text-gray-500">
                            You haven't dismissed any insights yet.
                        </div>
                    )}
                    {dismissedInsights.map(insight => (
                        <InsightCard
                            key={insight.id}
                            insight={insight}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default InsightsIndexPage;

